// pages/Finance_And_Account.tsx

import React from 'react';
import { motion , Variants , easeOut} from 'framer-motion';
import { Link } from 'react-router-dom';

// --- Animation Variants (Re-used and adapted) ---
const fadeIn: Variants = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.42, 0, 1, 1], // ✅ Equivalent to "easeOut"
    },
  },
};


export const slideInFromLeft: Variants = {
  hidden: { opacity: 0, x: -60 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.8,
      ease: [0.42, 0, 1, 1], // ✅ TS-safe bezier for "easeOut"
    },
  },
};

export const slideInFromRight: Variants = {
  hidden: { opacity: 0, x: 60 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.8,
      ease: [0.42, 0, 1, 1], // ✅
    },
  },
};

const staggerItems = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.2,
    },
  },
};

const itemScaleUp: Variants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.5 }
  },
  hover: {
    scale: 1.05,
    transition: {
      type: "spring" as const, // ✅ Strongly typed
      stiffness: 300
    }
  }
};

export const textPopUp: Variants = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.42, 0, 1, 1], // ✅ cubic-bezier form of "easeOut"
    },
  },
};

export const rotatePulse: Variants = {
  hidden: {
    opacity: 0,
    rotate: -15,
    scale: 0.8,
  },
  visible: {
    opacity: 1,
    rotate: 0,
    scale: 1,
    transition: {
      duration: 0.7,
      ease: [0.42, 0, 1, 1], // ✅ instead of 'easeOut'
    },
  },
};

const FinanceAndAccountPage: React.FC = () => {
  return (
    <>
  
        {/* SEO Title: Crucial for search results and browser tabs */}
        <title>Expert Finance & Accounting Services | [Your Company Name] | Noida, India</title>

        {/* Meta Description: A concise summary of the page content for search engines */}
        <meta
          name="description"
          content="Unlock financial clarity and growth with expert finance and accounting services from [Your Company Name]. We provide bookkeeping, tax preparation, payroll, and financial planning solutions from Noida, India."
        />

        {/* Canonical Tag: Prevents duplicate content issues */}
        <link rel="canonical" href="https://yourwebsite.com/finance-and-account" />

        {/* Open Graph Tags for Social Media Sharing */}
        <meta property="og:title" content="Expert Finance & Accounting Services | [Your Company Name]" />
        <meta property="og:description" content="Comprehensive financial solutions including bookkeeping, tax, payroll, and financial planning for businesses." />
        <meta property="og:url" content="https://yourwebsite.com/finance-and-account" />
        <meta property="og:type" content="website" />
        {/* Replace with a relevant image for social sharing */}
        <meta property="og:image" content="https://yourwebsite.com/images/og-finance-account.jpg" />
        <meta property="og:image:alt" content="Finance and Accounting Solutions" />



      <div className="finance-account-page min-h-screen bg-gray-50 text-gray-800">

        {/* 1. Hero Section: Emphasizing Clarity & Growth */}
        <motion.section
          className="relative h-[450px] flex items-center justify-center text-center bg-black text-white overflow-hidden"
          initial="hidden"
          animate="visible"
          variants={fadeIn}
        >
          <div className="absolute inset-0 z-0">
            <img
              src="https://www.berlinsbi.com/uploads/sites/2/2021/12/7-very-good-reasons-to-do-master-s-in-finance.jpg" // Placeholder for your hero background image
              alt="Financial Reports and Growth Charts"
              className="opacity-30 w-full h-full object-cover absolute inset-0"
              loading="eager"
            />
            <div className="absolute inset-0 bg-black opacity-40"></div>
          </div>
          <div className="z-10 max-w-5xl px-6">
            <motion.h1
              className="text-4xl md:text-5xl font-extrabold leading-tight mb-4 drop-shadow-lg"
              variants={textPopUp}
            >
              Teamed up with India's Expert Financial Accountants
            </motion.h1>
            <motion.p
              className="text-xl md:text-2xl mb-8 opacity-90 max-w-3xl mx-auto"
              variants={textPopUp}
              transition={{ delay: 0.3 }}
            >
              Delegate Your Financial Accounting Needs to Certified Experts
            </motion.p>
            <motion.button
              className="bg-blue-700 hover:bg-blue-500 text-white font-bold py-3 px-8 rounded-2xl shadow-lg transform transition duration-300 ease-in-out hover:scale-105"
              variants={fadeIn}
              transition={{ delay: 0.6, duration: 0.5 }}
              whileHover={{ scale: 1.05, boxShadow: '0 10px 20px rgba(0,0,0,0.2)' }}
              whileTap={{ scale: 0.95 }}
              aria-label="Get a free finance consultation"
            >
             <Link
  to="/contact"
  className="text-lg font-semibold bg-blue-700 hover:bg-blue-800 text-white py-2 px-4 rounded-full bg-clip-text text-transparent "
> 
Get a Free Financial Consultation
</Link>
 
             
            </motion.button>
          </div>
        </motion.section>

        ---

        {/* 2. Benefits of Our Financial Partnership Section */}
        <motion.section
          className="py-16 px-8 bg-white"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.4 }}
          variants={staggerItems}
        >
          <motion.h2
            className="text-4xl font-bold text-center mb-12 text-blue-800"
            variants={fadeIn}
          >
            Benefits of Our Financial Partnership
          </motion.h2>
          <div className="max-w-6xl mx-auto grid md:grid-cols-2 lg:grid-cols-3 gap-10">
            {/* Benefit Card 1: Time Savings */}
            <motion.div
              className="bg-gray-100 p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 transform hover:-translate-y-2 flex flex-col items-center text-center border border-gray-200"
              variants={itemScaleUp}
              whileHover="hover"
            >
              <motion.div
                className="text-5xl text-blue-600 mb-4"
                initial={{ rotate: 0 }}
                whileInView={{ rotate: [0, 10, -10, 0], transition: { duration: 1, ease: 'easeOut' } }}
                viewport={{ once: true }}
              >
                <i className="fas fa-hourglass-half"></i> {/* Font Awesome icon */}
              </motion.div>
              <h3 className="text-2xl font-semibold mb-3 text-orange-600">Save Time & Resources</h3>
              <p className="text-gray-600">Free up valuable time and internal resources by outsourcing your financial management to our experts.</p>
            </motion.div>

            {/* Benefit Card 2: Accuracy */}
            <motion.div
              className="bg-gray-100 p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 transform hover:-translate-y-2 flex flex-col items-center text-center border border-gray-200"
              variants={itemScaleUp}
              whileHover="hover"
            >
              <motion.div
                className="text-5xl text-purple-600 mb-4"
                initial={{ scale: 0.8 }}
                whileInView={{ scale: 1, transition: { duration: 0.5, ease: 'easeOut' } }}
                viewport={{ once: true }}
              >
                <i className="fas fa-check-circle"></i>
              </motion.div>
              <h3 className="text-2xl font-semibold mb-3 text-orange-600">Guaranteed Accuracy & Compliance</h3>
              <p className="text-gray-600">Ensure meticulous record-keeping, accurate reporting, and adherence to all financial regulations.</p>
            </motion.div>

            {/* Benefit Card 3: Cost-Effective */}
            <motion.div
              className="bg-gray-100 p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 transform hover:-translate-y-2 flex flex-col items-center text-center border border-gray-200"
              variants={itemScaleUp}
              whileHover="hover"
            >
              <motion.div
                className="text-5xl text-orange-600 mb-4"
                initial={{ x: -10, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1, transition: { duration: 0.6, ease: 'easeOut' } }}
                viewport={{ once: true }}
              >
                <i className="fas fa-hand-holding-usd"></i>
              </motion.div>
              <h3 className="text-2xl font-semibold mb-3 text-orange-600">Cost-Effective Expertise</h3>
              <p className="text-gray-600">Access senior-level financial expertise without the overhead of a full-time in-house department.</p>
            </motion.div>

            {/* Benefit Card 4: Strategic Insights */}
            <motion.div
              className="bg-gray-100 p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 transform hover:-translate-y-2 flex flex-col items-center text-center border border-gray-200"
              variants={itemScaleUp}
              whileHover="hover"
            >
              <motion.div
                className="text-5xl text-red-600 mb-4"
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1, transition: { duration: 0.6, ease: 'easeOut' } }}
                viewport={{ once: true }}
              >
                <i className="fas fa-chart-pie"></i>
              </motion.div>
              <h3 className="text-2xl font-semibold mb-3 text-orange-600">Actionable Financial Insights</h3>
              <p className="text-gray-600">Gain clear, insightful financial reports and analysis to support strategic decision-making and growth.</p>
            </motion.div>

            {/* Benefit Card 5: Scalability */}
            <motion.div
              className="bg-gray-100 p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 transform hover:-translate-y-2 flex flex-col items-center text-center border border-gray-200"
              variants={itemScaleUp}
              whileHover="hover"
            >
              <motion.div
                className="text-5xl text-teal-600 mb-4"
                initial={{ rotate: -15, opacity: 0 }}
                whileInView={{ rotate: 0, opacity: 1, transition: { duration: 0.6, ease: 'easeOut' } }}
                viewport={{ once: true }}
              >
                <i className="fas fa-ruler-combined"></i>
              </motion.div>
              <h3 className="text-2xl font-semibold mb-3 text-orange-600">Scalable Support as You Grow</h3>
              <p className="text-gray-600">Our services adapt to your business needs, whether you're a startup or an expanding enterprise.</p>
            </motion.div>

            {/* Benefit Card 6: Focus on Core Business */}
            <motion.div
              className="bg-gray-100 p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 transform hover:-translate-y-2 flex flex-col items-center text-center border border-gray-200"
              variants={itemScaleUp}
              whileHover="hover"
            >
              <motion.div
                className="text-5xl text-indigo-600 mb-4"
                initial={{ scaleY: 0 }}
                whileInView={{ scaleY: 1, transition: { duration: 0.6, ease: 'easeOut' } }}
                viewport={{ once: true }}
              >
                <i className="fas fa-briefcase"></i>
              </motion.div>
              <h3 className="text-2xl font-semibold mb-3 text-orange-600">Focus on Your Business Core</h3>
              <p className="text-gray-600">Delegate complex financial tasks to us, allowing you to dedicate your energy to core business operations.</p>
            </motion.div>
          </div>
        </motion.section>

        ---

        {/* 3. Our Comprehensive Finance & Accounting Services (Service Grid) */}
        <motion.section
          className="py-16 px-8 bg-blue-50"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeIn}
        >
          <motion.h2
            className="text-4xl font-bold text-center mb-12 text-blue-800"
            variants={fadeIn}
          >
            Our Comprehensive Finance & Accounting Services
          </motion.h2>
          <div className="max-w-7xl mx-auto grid md:grid-cols-2 lg:grid-cols-3 gap-10">
            {/* Service 1: Bookkeeping & Record Keeping */}
            <motion.div
              className="bg-white rounded-lg shadow-xl p-8 flex flex-col items-center text-center border-t-4 border-orange-500"
              variants={itemScaleUp}
              whileHover="hover"
            >
              <img
                src="https://img.freepik.com/free-vector/people-working-with-data-analysis_23-2148408011.jpg?t=st=1718274719~exp=1718278319~hmac=ee2e4b0051e0640c6a83606f71d5308b4ed7f9754f15ddf0f63198e3b5e4aa34&w=740" // Placeholder image
                alt="Bookkeeping and Financial Record Keeping"
                className="w-full h-48 object-cover rounded-md mb-6"
                loading="lazy"
              />
              <h3 className="text-2xl font-bold mb-3 text-gray-900">Dedicated Employees</h3>
              <p className="text-lg text-gray-700 mb-4">
               Our Finance virtual staff members are incredibly committed, providing individualized, dependable support, guaranteeing smooth operations, and providing unrivaled attention to the needs of dedicated professionals.
              </p>
    
            </motion.div>

            {/* Service 2: Tax Preparation & Planning */}
            <motion.div
              className="bg-white rounded-lg shadow-xl p-8 flex flex-col items-center text-center border-t-4 border-orange-500"
              variants={itemScaleUp}
              whileHover="hover"
            >
              <img
                src="https://img.freepik.com/free-vector/tax-submission-concept-illustration_114360-4498.jpg?t=st=1718274776~exp=1718278376~hmac=565551f33f2b3e8e4544d62c9540b79374092b67f40f06f70a927063f7041f0f&w=740" // Placeholder image
                alt="Tax Preparation and Financial Planning"
                className="w-full h-48 object-cover rounded-md mb-6"
                loading="lazy"
              />
              <h3 className="text-2xl font-bold mb-3 text-gray-900">Multiple Skills</h3>
              <p className="text-lg text-gray-700 mb-4">
               a highly qualified virtual staff in the Finance field who is adept at managing electronic Finance records, scheduling patients, billing, research, and customer service.
              </p>
             
            </motion.div>

            {/* Service 3: Payroll Management */}
            <motion.div
              className="bg-white rounded-lg shadow-xl p-8 flex flex-col items-center text-center border-t-4 border-orange-500"
              variants={itemScaleUp}
              whileHover="hover"
            >
              <img
                src="https://img.freepik.com/free-vector/employee-salary-comparison-concept-illustration_114360-1558.jpg?t=st=1718274820~exp=1718278420~hmac=401345d315904d9c7d0d0f4d36ef554a938c3505b3e6488d011153205060775d&w=740" // Placeholder image
                alt="Payroll Processing Services"
                className="w-full h-48 object-cover rounded-md mb-6"
                loading="lazy"
              />
              <h3 className="text-2xl font-bold mb-3 text-gray-900">100% Cost-Effective</h3>
              <p className="text-lg text-gray-700 mb-4">
                Hiring virtual employees is a smart, cost-effective choice that helps you save on expenses like office space and utilities. It allows you to maintain a flexible, productive team without the overhead of traditional in-office staff.
              </p>
             
            </motion.div>

            {/* Service 4: Financial Reporting & Analysis */}
            <motion.div
              className="bg-white rounded-lg shadow-xl p-8 flex flex-col items-center text-center border-t-4 border-orange-500"
              variants={itemScaleUp}
              whileHover="hover"
            >
              <img
                src="https://img.freepik.com/free-vector/investment-dashboard-with-charts-analysis_23-2148386348.jpg?t=st=1718274872~exp=1718278472~hmac=6217431e6702e0b514ee673620f3408a2fc10a62a7d4ee758d4a670415309325&w=740" // Placeholder image
                alt="Financial Reporting and Business Analysis"
                className="w-full h-48 object-cover rounded-md mb-6"
                loading="lazy"
              />
              <h3 className="text-2xl font-bold mb-3 text-gray-900">Free Trail</h3>
              <p className="text-lg text-gray-700 mb-4">
                Still deciding if we're the right choice? Test our capabilities before making a decision. Move forward only when you're fully confident in our expertise
              </p>
              
            </motion.div>

            {/* Service 5: CFO Services & Financial Advisory */}
            <motion.div
              className="bg-white rounded-lg shadow-xl p-8 flex flex-col items-center text-center border-t-4 border-orange-500"
              variants={itemScaleUp}
              whileHover="hover"
            >
              <img
                src="https://img.freepik.com/free-vector/personal-finance-advisor-concept-illustration_114360-2442.jpg?t=st=1718274932~exp=1718278532~hmac=80775d7967215f799ff632e8d97607a9e3ee3165b4c48316c561b36e0906a5b2&w=740" // Placeholder image
                alt="CFO Services and Financial Advisory"
                className="w-full h-48 object-cover rounded-md mb-6"
                loading="lazy"
              />
              <h3 className="text-2xl font-bold mb-3 text-gray-900">Easy Communation</h3>
              <p className="text-lg text-gray-700 mb-4">
                In order to guarantee seamless coordination, our service provides quick responses, easy communications, and round-the-clock support.
              </p>
              
            </motion.div>

            {/* Service 6: Audit Support & Compliance */}
            <motion.div
              className="bg-white rounded-lg shadow-xl p-8 flex flex-col items-center text-center border-t-4 border-orange-500"
              variants={itemScaleUp}
              whileHover="hover"
            >
              <img
                src="https://img.freepik.com/free-vector/internal-audit-concept-illustration_114360-7053.jpg?t=st=1718274972~exp=1718278572~hmac=c391307b8b2111ee859c7885b9b8b2b621e257b4946779430c558d7c2a71d798&w=740" // Placeholder image
                alt="Audit Support and Compliance Services"
                className="w-full h-48 object-cover rounded-md mb-6"
                loading="lazy"
              />
              <h3 className="text-2xl font-bold mb-3 text-gray-900">Financial Advisory Services</h3>
              <p className="text-lg text-gray-700 mb-4">
               Our virtual assistant provides expert financial advisory services, helping you manage budgets, investments, and financial planning for better decision-making.
              </p>
             
            </motion.div>
          </div>
        </motion.section>

        ---

       <motion.section
  className="py-16 px-8 bg-gradient-to-br from-gray-100 to-gray-200"
  initial="hidden"
  whileInView="visible"
  viewport={{ once: true, amount: 0.3 }}
  variants={staggerItems}
>
  <motion.h2
    className="text-4xl font-bold text-center mb-12 text-blue-800"
    variants={fadeIn}
  >
    Our Seamless Financial Process
  </motion.h2>
  <div className="max-w-6xl mx-auto relative">
    {/* Horizontal Timeline Connector (for larger screens) */}
    <div className="absolute top-1/2 left-0 right-0 h-1 bg-purple-300 hidden md:block z-0"></div>

    <div className="grid md:grid-cols-5 gap-y-12 md:gap-x-8 items-start relative z-10">
      {/* Step 1 */}
      <motion.div
        className="flex flex-col items-center text-center p-4 bg-white rounded-lg shadow-md border-b-4 border-blue-500"
        variants={itemScaleUp}
      >
        <div className="w-12 h-12 rounded-full bg-orange-500 text-white flex flex-col items-center justify-center text-2xl font-bold shadow-lg mb-4">
          <span className="text-xl">1</span>
          <i className="fas fa-handshake text-2xl"></i>
        </div>
        <h3 className="text-xl font-bold mb-2 text-gray-900">1. Initial Consultation</h3>
        <p className="text-sm text-gray-700">We discuss your financial needs, current challenges, long-term goals, and business objectives.  </p>
      </motion.div>

      {/* Step 2 */}
      <motion.div
        className="flex flex-col items-center text-center p-4 bg-white rounded-lg shadow-md border-b-4 border-blue-500"
        variants={itemScaleUp}
        transition={{ delay: 0.2 }}
      >
        <div className="w-12 h-12 rounded-full bg-orange-500 text-white flex flex-col items-center justify-center text-2xl font-bold shadow-lg mb-4">
          <span className="text-xl">2</span>
          <i className="fas fa-file-invoice-dollar text-2xl"></i>
        </div>
        <h3 className="text-xl font-bold mb-2 text-gray-900">2. Tailored Proposal</h3>
        <p className="text-sm text-gray-700">We provide a customized service proposal outlining scope, timelines, and pricing.</p>
      </motion.div>

      {/* Step 3 */}
      <motion.div
        className="flex flex-col items-center text-center p-4 bg-white rounded-lg shadow-md border-b-4 border-blue-500"
        variants={itemScaleUp}
        transition={{ delay: 0.4 }}
      >
        <div className="w-12 h-12 rounded-full bg-orange-500 text-white flex flex-col items-center justify-center text-2xl font-bold shadow-lg mb-4">
          <span className="text-xl">3</span>
          <i className="fas fa-clipboard-check text-2xl"></i>
        </div>
        <h3 className="text-xl font-bold mb-2 text-gray-900">3. Onboarding & Setup</h3>
        <p className="text-sm text-gray-700">Seamless setup of systems, data migration, and team introduction for smooth transition.</p>
      </motion.div>

      {/* Step 4 */}
      <motion.div
        className="flex flex-col items-center text-center p-4 bg-white rounded-lg shadow-md border-b-4 border-blue-500"
        variants={itemScaleUp}
        transition={{ delay: 0.6 }}
      >
        <div className="w-12 h-12 rounded-full bg-orange-500 text-white flex flex-col items-center justify-center text-2xl font-bold shadow-lg mb-4">
          <span className="text-xl">4</span>
          <i className="fas fa-calculator text-2xl"></i>
        </div>
        <h3 className="text-xl font-bold mb-2 text-gray-900">4. Execution & Reporting</h3>
        <p className="text-sm text-gray-700">Our team actively manages your finances with regular reports and transparent communication.</p>
      </motion.div>

      {/* Step 5 */}
      <motion.div
        className="flex flex-col items-center text-center p-4 bg-white rounded-lg shadow-md border-b-4 border-blue-500"
        variants={itemScaleUp}
        transition={{ delay: 0.8 }}
      >
        <div className="w-12 h-12 rounded-full bg-orange-500 text-white flex flex-col items-center justify-center text-2xl font-bold shadow-lg mb-4">
          <span className="text-xl">5</span>
          <i className="fas fa-chart-line text-2xl"></i>
        </div>
        <h3 className="text-xl font-bold mb-2 text-gray-900">5. Continuous Advisory</h3>
        <p className="text-sm text-gray-700">Ongoing support, proactive advice, and performance reviews to foster continuous improvement.</p>
      </motion.div>
    </div>
  </div>
</motion.section>
        ---

        {/* 5. Client Success Stories Section */}
        {/* <motion.section
          className="py-16 px-8 bg-blue-600 text-white"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={staggerItems}
        >
          <motion.h2
            className="text-4xl font-bold text-center mb-12"
            variants={fadeIn}
          >
            What Our Clients Say About Our Financial Expertise
          </motion.h2>
          <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-10">
            
            <motion.div
              className="bg-white p-8 rounded-lg shadow-lg flex flex-col items-center text-center text-gray-800"
              variants={itemScaleUp}
              whileHover="hover"
            >
              <motion.div
                className="w-24 h-24 rounded-full overflow-hidden mb-4 border-4 border-teal-500 flex justify-center items-center"
                initial={{ scale: 0 }}
                whileInView={{ scale: 1, transition: { duration: 0.5, type: 'spring' } }}
                viewport={{ once: true }}
              >
                <img
                  src="/images/avatar-finance-client-1.jpg" // Placeholder for client avatar
                  alt="Avatar of Priya Sharma, Startup Founder"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </motion.div>
              <p className="text-lg italic mb-4">
                &quot;**[Your Company Name]** transformed our financial operations. Their bookkeeping is meticulous, and their tax planning saved us a significant amount. Truly reliable!&quot;
              </p>
              <p className="font-semibold text-blue-700">- Priya Sharma, Founder of InnovateFlow</p>
            </motion.div>

          
            <motion.div
              className="bg-white p-8 rounded-lg shadow-lg flex flex-col items-center text-center text-gray-800"
              variants={itemScaleUp}
              whileHover="hover"
            >
              <motion.div
                className="w-24 h-24 rounded-full overflow-hidden mb-4 border-4 border-green-500 flex justify-center items-center"
                initial={{ scale: 0 }}
                whileInView={{ scale: 1, transition: { duration: 0.5, type: 'spring', delay: 0.2 } }}
                viewport={{ once: true }}
              >
                <img
                  src="/images/avatar-finance-client-2.jpg" // Placeholder for client avatar
                  alt="Avatar of Rajesh Kumar, SME Owner"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </motion.div>
              <p className="text-lg italic mb-4">
                &quot;Their financial advisory services are invaluable. We now have a clear picture of our cash flow and profitability, enabling us to make better business decisions.&quot;
              </p>
              <p className="font-semibold text-blue-700">- Rajesh Kumar, Owner of Global Traders</p>
            </motion.div>
          </div>
        </motion.section> */}

        ---

        {/* 6. Frequently Asked Questions (FAQ) Section */}
        <motion.section
          className="py-16 px-8 bg-gray-100"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeIn}
        >
          <motion.h2
            className="text-4xl font-bold text-center mb-12 text-blue-800"
            variants={fadeIn}
          >
            Common Questions About Our Services
          </motion.h2>
          <div className="max-w-4xl mx-auto space-y-6">
            <motion.details
              className="bg-white p-6 rounded-lg shadow-md cursor-pointer border border-gray-200"
              variants={textPopUp}
            >
              <summary className="text-xl font-semibold text-gray-900 flex justify-between items-center">
                What types of businesses do you serve?
                <i className="fas fa-chevron-down text-blue-900">+</i>
              </summary>
              <p className="text-gray-700 mt-4 leading-relaxed">
                We cater to a wide range of businesses, from **startups and SMEs** to larger corporations, across various industries. Our services are adaptable to your specific business size and needs.
              </p>
            </motion.details>

            <motion.details
              className="bg-white p-6 rounded-lg shadow-md cursor-pointer border border-gray-200"
              variants={textPopUp}
              transition={{ delay: 0.1 }}
            >
              <summary className="text-xl font-semibold text-gray-900 flex justify-between items-center">
                How do you ensure data security and confidentiality of my financial information?
                <i className="fas fa-chevron-down text-blue-900">+</i>
              </summary>
              <p className="text-gray-700 mt-4 leading-relaxed">
                We employ robust security measures, including encrypted systems, strict access controls, and comprehensive confidentiality agreements. Your financial data's security is our top priority.
              </p>
            </motion.details>

            <motion.details
              className="bg-white p-6 rounded-lg shadow-md cursor-pointer border border-gray-200"
              variants={textPopUp}
              transition={{ delay: 0.2 }}
            >
              <summary className="text-xl font-semibold text-gray-900 flex justify-between items-center">
                Can you help with international tax compliance for businesses in Noida?
                <i className="fas fa-chevron-down text-blue-900">+</i>
              </summary>
              <p className="text-gray-700 mt-4 leading-relaxed">
                Yes, our team has experience with both domestic (Indian) and international tax regulations. We can assist businesses in **Noida** and beyond with cross-border tax compliance and planning, helping you navigate complexities like **transfer pricing** and **double taxation treaties**.
              </p>
            </motion.details>

            <motion.details
              className="bg-white p-6 rounded-lg shadow-md cursor-pointer border border-gray-200"
              variants={textPopUp}
              transition={{ delay: 0.3 }}
            >
              <summary className="text-xl font-semibold text-gray-900 flex justify-between items-center">
                What software do you use for accounting and reporting?
                <i className="fas fa-chevron-down text-blue-900">+</i>
              </summary>
              <p className="text-gray-700 mt-4 leading-relaxed">
                We are proficient in various industry-standard accounting software, including TallyPrime, QuickBooks, Zoho Books, and more. We can work with your existing system or recommend the best solution for your needs.
              </p>
            </motion.details>
          </div>
        </motion.section>

        {/* JSON-LD Schema for FAQ Page */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": [
                {
                  "@type": "Question",
                  "name": "What types of businesses do you serve?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "We cater to a wide range of businesses, from startups and SMEs to larger corporations, across various industries. Our services are adaptable to your specific business size and needs."
                  }
                },
                {
                  "@type": "Question",
                  "name": "How do you ensure data security and confidentiality of my financial information?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "We employ robust security measures, including encrypted systems, strict access controls, and comprehensive confidentiality agreements. Your financial data's security is our top priority."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Can you help with international tax compliance for businesses in Noida?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes, our team has experience with both domestic (Indian) and international tax regulations. We can assist businesses in Noida and beyond with cross-border tax compliance and planning, helping you navigate complexities like transfer pricing and double taxation treaties."
                  }
                },
                {
                  "@type": "Question",
                  "name": "What software do you use for accounting and reporting?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "We are proficient in various industry-standard accounting software, including TallyPrime, QuickBooks, Zoho Books, and more. We can work with your existing system or recommend the best solution for your needs."
                  }
                }
              ]
            }
          `}
        </script>

        ---

        {/* 7. Final Call to Action Section
        <motion.section
          className="py-20 px-8 bg-gradient-to-r from-teal-700 to-green-800 text-white text-center"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.5 }}
          variants={fadeIn}
        >
          <motion.h2
            className="text-4xl md:text-5xl font-extrabold mb-6 drop-shadow-md"
            variants={textPopUp}
          >
            Virtual Assistant for Accurate Accounting and Tax Advisory Services
          </motion.h2>
          <motion.p
            className="text-xl md:text-2xl mb-10 opacity-90 max-w-3xl mx-auto"
            variants={textPopUp}
            transition={{ delay: 0.3 }}
          >
            Our virtual assistant provides precise accounting and expert tax advisory services to streamline your finances.
          </motion.p>
          <motion.button
            className="bg-purple-500 hover:bg-purple-600 text-white font-bold py-4 px-10 rounded-full shadow-xl transform transition duration-300 ease-in-out hover:scale-105 text-xl"
            variants={fadeIn}
            transition={{ delay: 0.6, duration: 0.5 }}
            whileHover={{ scale: 1.05, boxShadow: '0 12px 24px rgba(0,0,0,0.3)' }}
            whileTap={{ scale: 0.95 }}
            aria-label="Request a free finance and accounting quote"
          >
            Request Your Free Quote Today
          </motion.button>
        </motion.section> */}

      </div>
    </>
  );
};

export default FinanceAndAccountPage;